import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ISerialModel } from "../../webapi/service/getLicences";
import MemoText from "./MemoText";
import EditButton from "../common/EditButton";
import SaveButton from "../common/SaveButton";
import CancelButton from "../common/CancelButton";

/**
 * 最大メモ長
 */
export const MaxMemoLength = 120;

export interface IProps {
  /**
   * シリアル情報
   */
  serials: ISerialModel[];
  /**
   * シリアル情報更新
   */
  onUpdateSerial: (serial: string, memo: string) => void;
}

interface SerialEditInfo {
  /**
   * シリアル番号
   */
  serial: string;
  /**
   * メモ
   */
  memo: string;
  /**
   * 編集中の場合true
   */
  edit: boolean;
  /**
   * undo文字列
   */
  undo: string;
}

/**
 * シリアルリストElement
 * @param props - シリアルリストパラメータ
 * @returns シリアルリストElement
 */
export default function SerialList(props: IProps) {
  const { t } = useTranslation();
  const [editInfos, setEditInfos] = useState<SerialEditInfo[]>([]);

  useEffect(() => {
    const editInfos = props.serials.map((serial) => {
      return {
        serial: serial.serial,
        memo: serial.memo,
        edit: false,
        undo: "",
      };
    });
    setEditInfos(editInfos);
  }, [props.serials]);

  function handleClickEdit(serial: string) {
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.serial === serial;
    });
    if (find) {
      find.edit = true;
      find.undo = find.memo;
      setEditInfos(slice);
    }
  }

  function handleClickSave(serial: string) {
    const find = editInfos.find((info) => {
      return info.serial === serial;
    });
    if (find) {
      props.onUpdateSerial(serial, find.memo);
    }
  }

  function handleClickCancel(serial: string) {
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.serial === serial;
    });
    if (find) {
      find.edit = false;
      find.memo = find.undo;
      find.undo = "";
      setEditInfos(slice);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    const slice = editInfos.slice();
    const find = slice.find((info) => {
      return info.serial === id;
    });
    if (find) {
      find.memo = value;
      setEditInfos(slice);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      const { id, value } = e.target as HTMLInputElement;
      props.onUpdateSerial(id, value);
    }
  }

  const body = editInfos.map((info, i) => {
    const { serial, memo, edit } = info;
    const memoText = edit ? (
      <Form.Control
        style={{ width: "290px" }}
        id={serial}
        defaultValue={memo}
        maxLength={MaxMemoLength}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    ) : (
      <MemoText text={memo} />
    );
    const editButton = edit ? (
      <div style={{ display: "flex" }}>
        <SaveButton onClick={() => handleClickSave(serial)} />
        <CancelButton onClick={() => handleClickCancel(serial)} />
      </div>
    ) : (
      <EditButton onClick={() => handleClickEdit(serial)} />
    );
    return (
      <tr key={i}>
        <td>{info.serial}</td>
        <td style={{ width: "250px" }}>{memoText}</td>
        <td style={{ width: "40px" }}>{editButton}</td>
      </tr>
    );
  });

  return (
    <table>
      <thead>
        <tr>
          <th>{t("license_serial_list")}</th>
          <th colSpan={2}>{t("license_serial_memo")}</th>
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </table>
  );
}
